import { useState, useEffect, useCallback } from 'react';
import { FieldList } from './FieldList';
import { FieldEditor } from './FieldEditor';
import { FormTextArea } from '../shared/form/FormTextArea';
import type { FieldDefinition } from '../../utils/schemaBuilder';
import {
  fieldsToJsonSchema,
  jsonSchemaToFields,
  generateFieldId,
} from '../../utils/schemaBuilder';

type ViewMode = 'visual' | 'json';

interface SchemaBuilderProps {
  value: Record<string, unknown> | null;
  onChange: (schema: Record<string, unknown> | null) => void;
  label?: string;
  error?: string;
  showRawToggle?: boolean;
  readOnly?: boolean;
}

export function SchemaBuilder({
  value,
  onChange,
  label = 'Input Schema',
  error,
  showRawToggle = true,
  readOnly = false,
}: SchemaBuilderProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('visual');
  const [fields, setFields] = useState<FieldDefinition[]>([]);
  const [editingField, setEditingField] = useState<FieldDefinition | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState<string | undefined>();

  // Initialize fields from value
  useEffect(() => {
    const newFields = jsonSchemaToFields(value);
    setFields(newFields);
    setJsonText(value ? JSON.stringify(value, null, 2) : '');
  }, [value]);

  // Update parent when fields change (visual mode)
  const updateSchemaFromFields = useCallback((newFields: FieldDefinition[]) => {
    const schema = fieldsToJsonSchema(newFields);
    onChange(schema);
    setJsonText(schema ? JSON.stringify(schema, null, 2) : '');
  }, [onChange]);

  // Handle JSON text changes
  const handleJsonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    setJsonText(text);

    if (!text.trim()) {
      setJsonError(undefined);
      onChange(null);
      setFields([]);
      return;
    }

    try {
      const parsed = JSON.parse(text);
      setJsonError(undefined);
      onChange(parsed);
      setFields(jsonSchemaToFields(parsed));
    } catch {
      setJsonError('Invalid JSON format');
    }
  };

  // Field operations
  const handleAddField = () => {
    setEditingField(null);
    setIsEditorOpen(true);
  };

  const handleEditField = (field: FieldDefinition) => {
    setEditingField(field);
    setIsEditorOpen(true);
  };

  const handleDeleteField = (fieldId: string) => {
    const newFields = fields.filter(f => f.id !== fieldId);
    setFields(newFields);
    updateSchemaFromFields(newFields);
  };

  const handleSaveField = (field: FieldDefinition) => {
    let newFields: FieldDefinition[];

    if (editingField) {
      // Update existing field
      newFields = fields.map(f => f.id === editingField.id ? field : f);
    } else {
      // Add new field
      newFields = [...fields, { ...field, id: generateFieldId() }];
    }

    setFields(newFields);
    updateSchemaFromFields(newFields);
    setIsEditorOpen(false);
    setEditingField(null);
  };

  const handleCancelEdit = () => {
    setIsEditorOpen(false);
    setEditingField(null);
  };

  // Switch view modes
  const handleToggleMode = () => {
    if (viewMode === 'json' && jsonError) {
      // Don't switch to visual if JSON is invalid
      return;
    }
    setViewMode(viewMode === 'visual' ? 'json' : 'visual');
  };

  const existingNames = fields
    .filter(f => f.id !== editingField?.id)
    .map(f => f.name);

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {label}
        </label>
        <div className="flex items-center gap-2">
          {showRawToggle && (
            <button
              type="button"
              onClick={handleToggleMode}
              disabled={viewMode === 'json' && !!jsonError}
              className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {viewMode === 'visual' ? (readOnly ? 'View as JSON' : 'Edit as JSON') : 'Use Visual Builder'}
            </button>
          )}
          {viewMode === 'visual' && !readOnly && (
            <button
              type="button"
              onClick={handleAddField}
              className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="w-3.5 h-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Field
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      {viewMode === 'visual' ? (
        <FieldList
          fields={fields}
          onEdit={handleEditField}
          onDelete={handleDeleteField}
          readOnly={readOnly}
        />
      ) : (
        <FormTextArea
          label=""
          name="schemaJson"
          value={jsonText}
          onChange={handleJsonChange}
          error={jsonError}
          placeholder='{"type": "object", "properties": {...}}'
          rows={10}
          className="font-mono text-sm"
          disabled={readOnly}
        />
      )}

      {/* Error from parent */}
      {error && !jsonError && (
        <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>
      )}

      {/* Field count indicator */}
      {viewMode === 'visual' && fields.length > 0 && (
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          {fields.length} field{fields.length !== 1 ? 's' : ''} defined
          {fields.filter(f => f.required).length > 0 && (
            <span> ({fields.filter(f => f.required).length} required)</span>
          )}
        </p>
      )}

      {/* Field Editor Modal */}
      {isEditorOpen && (
        <FieldEditor
          field={editingField}
          existingNames={existingNames}
          onSave={handleSaveField}
          onCancel={handleCancelEdit}
        />
      )}
    </div>
  );
}
